import { useEffect, useMemo, useState } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';

import { AnalyticsEmpty, AnalyticsError, AnalyticsLoading } from '../../components/AnalyticsState';
import AnalyticsTabs from '../../components/AnalyticsTabs';
import { SectionSubtitle, SectionTitle } from '../../components/analyticsUi';
import InfoTooltip from 'components/v2/common/infoTooltip';

import { OPERATIONS_MESSAGES_POSTED } from '../../constants/metricDescriptions';
import useRoleTypeTabs, { ROLE_TYPE_LABELS } from '../../hooks/useRoleTypeTabs';
import { getApplicantPostedMessages, getPractitionerMessagesPosted } from 'api/rest/analytics';
import { AnalyticsRoleType } from 'hooks/analytics';

const CLIENT_TAB = 'client' as const;
type MessagesPostedTab = AnalyticsRoleType | typeof CLIENT_TAB;

const TAB_LABELS: Record<MessagesPostedTab, string> = {
    ...ROLE_TYPE_LABELS,
    [CLIENT_TAB]: 'Clients',
};

type MessagesPostedRow = {
    id: number;
    name: string;
    messagesPosted: number;
};

type SortDirection = 'asc' | 'desc';
const DEFAULT_DIRECTION: SortDirection = 'desc';

interface MessagesPostedProps {
    from?: string;
    to?: string;
    serviceProviderId?: string;
}

const MessagesPosted = ({ from, to, serviceProviderId }: MessagesPostedProps) => {
    const { allowedTabs: allowedRoleTabs, defaultTab: defaultRoleTab } = useRoleTypeTabs();

    const messagesPostedTabs = useMemo<MessagesPostedTab[]>(
        () => (allowedRoleTabs.length > 0 ? [...allowedRoleTabs, CLIENT_TAB] : []),
        [allowedRoleTabs]
    );

    const [selectedTab, setSelectedTab] = useState<MessagesPostedTab | null>(defaultRoleTab);
    const [messages, setMessages] = useState<MessagesPostedRow[]>([]);
    const [loading, setLoading] = useState<boolean>(messagesPostedTabs.length > 0);
    const [error, setError] = useState<string | null>(null);
    const [direction, setDirection] = useState<SortDirection>(DEFAULT_DIRECTION);

    useEffect(() => {
        if (selectedTab && !messagesPostedTabs.includes(selectedTab)) {
            setSelectedTab(defaultRoleTab);
        }
    }, [messagesPostedTabs, selectedTab, defaultRoleTab]);

    useEffect(() => {
        if (!selectedTab || !messagesPostedTabs.includes(selectedTab)) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);

        const request =
            selectedTab === CLIENT_TAB
                ? getApplicantPostedMessages(from, to, serviceProviderId).then((res) =>
                      (res.data.applicantPostedMessages ?? []).map((item) => ({
                          id: item.applicantId,
                          name: item.applicantName,
                          messagesPosted: item.messagesPosted,
                      }))
                  )
                : getPractitionerMessagesPosted(selectedTab, from, to, serviceProviderId).then(
                      (res) =>
                          (res.data.practitionerMessagesPosted ?? []).map((item) => ({
                              id: item.practitionerId,
                              name: item.practitionerName,
                              messagesPosted: item.messagesPosted,
                          }))
                  );

        request
            .then((rows) => {
                if (cancelled) return;
                setMessages(rows);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(
                    err?.response?.data?.message || err.message || 'Failed to load Messages Posted.'
                );
                setMessages([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedTab, messagesPostedTabs, from, to, serviceProviderId]);

    const handleTabChange = (tab: MessagesPostedTab) => {
        if (tab === selectedTab) return;
        setSelectedTab(tab);
        setDirection(DEFAULT_DIRECTION);
    };

    const toggleSort = () => {
        setDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    };

    const sortedMessages = useMemo(() => {
        const copy = [...messages];
        copy.sort((a, b) => {
            const diff = a.messagesPosted - b.messagesPosted;
            return direction === 'desc' ? -diff : diff;
        });
        return copy;
    }, [messages, direction]);

    if (messagesPostedTabs.length === 0 || !selectedTab) return null;

    const isClientTab = selectedTab === CLIENT_TAB;
    const subjectColumnLabel = isClientTab ? 'Client' : 'Practitioner';
    const emptyStateMessage = isClientTab
        ? 'No client messages posted for this period.'
        : 'No messages posted for this period.';

    return (
        <Paper
            component="section"
            variant="outlined"
            sx={{ borderRadius: 2, p: 2, mb: 2 }}
            aria-labelledby="messages-posted-title"
        >
            <SectionTitle>
                <span id="messages-posted-title">Messages Posted</span>
                <InfoTooltip title={OPERATIONS_MESSAGES_POSTED} />
            </SectionTitle>
            <SectionSubtitle>
                Messages by team member or client in the selected period.
            </SectionSubtitle>
            <AnalyticsTabs
                ariaLabel="Messages posted team"
                selected={selectedTab}
                onChange={handleTabChange}
                tabs={messagesPostedTabs.map((tab) => ({
                    value: tab,
                    label: TAB_LABELS[tab],
                }))}
            />

            {loading ? (
                <AnalyticsLoading minHeight={160} />
            ) : error ? (
                <AnalyticsError message={error} />
            ) : sortedMessages.length === 0 ? (
                <AnalyticsEmpty title="No messages posted" description={emptyStateMessage} />
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{subjectColumnLabel}</TableCell>
                                <TableCell align="right" sortDirection={direction}>
                                    <TableSortLabel
                                        active
                                        direction={direction}
                                        onClick={toggleSort}
                                    >
                                        Messages
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedMessages.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell sx={{ fontWeight: 700 }}>{row.name}</TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}
                                    >
                                        {row.messagesPosted}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
};

export default MessagesPosted;
